import { z } from 'zod';

/**
 * HTTP methods supported by the contract system
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

/**
 * Base route definition - the "interface" for a single endpoint
 */
export interface RouteContract<
	TMethod extends HttpMethod = HttpMethod,
	TPath extends string = string,
	TParams extends z.ZodType | undefined = z.ZodType | undefined,
	TQuery extends z.ZodType | undefined = z.ZodType | undefined,
	THeaders extends z.ZodType | undefined = z.ZodType | undefined,
	TBody extends z.ZodType | undefined = z.ZodType | undefined,
	TResponse extends z.ZodType = z.ZodType,
	TContentType extends 'multipart/form-data' | undefined = 'multipart/form-data' | undefined,
> {
	method: TMethod;
	path: TPath;
	contentType?: TContentType;
	params?: TParams;
	query?: TQuery;
	headers?: THeaders;
	body?: TBody;
	response: TResponse;
	operationId?: string;
	tags?: string[];
	summary?: string;
	description?: string;
}

/**
 * A service contract - collection of route contracts
 */
export type ServiceContract = Record<string, RouteContract>;

/**
 * Infer the response type from a route contract
 */
export type InferResponse<T extends RouteContract> = z.infer<T['response']>;

/**
 * Input shape for route() - uses optional fields for params/query/headers/body
 */
type RouteDefinition = {
	method: HttpMethod;
	path: string;
	contentType?: 'multipart/form-data';
	params?: z.ZodType;
	query?: z.ZodType;
	headers?: z.ZodType;
	body?: z.ZodType;
	response: z.ZodType;
	operationId?: string;
	tags?: string[];
	summary?: string;
	description?: string;
};

/**
 * Extract a field from T if it exists as a ZodType, otherwise undefined.
 * Uses structural check: T extends { field: z.ZodType } is false when field is omitted.
 */
type ExtractField<T, K extends string> = T extends Record<K, z.ZodType> ? T[K] : undefined;

/**
 * Helper to create a route contract with full type inference.
 * Uses a single generic T to preserve which fields were actually provided.
 */
export function route<T extends RouteDefinition>(
	definition: T
): RouteContract<
	T['method'],
	T['path'] & string,
	ExtractField<T, 'params'>,
	ExtractField<T, 'query'>,
	ExtractField<T, 'headers'>,
	ExtractField<T, 'body'>,
	T['response'] & z.ZodType,
	T extends { contentType: 'multipart/form-data' } ? 'multipart/form-data' : undefined
> {
	return definition as any;
}

/**
 * Symbol for contract metadata — invisible to Object.entries/keys/values.
 * Used by createServiceClient to auto-discover contracts from barrel exports.
 */
export const CONTRACT_META = Symbol('contractMeta');

export interface ContractMeta<N extends string = string> {
	readonly name: N;
	readonly root?: boolean;
}

export interface DefineContractOptions {
	/** Mark as root contract — routes sit at service root, no prefix added. */
	root?: boolean;
}

/**
 * Helper to create a service contract.
 *
 * Named signature: defineContract(name, routes) — embeds metadata for auto-discovery.
 * The name doubles as the URL prefix (e.g. 'sequences' → /sequences).
 * Use { root: true } for contracts whose routes sit at the service root (no prefix).
 * Legacy signature: defineContract(routes) — backward compatible, no metadata.
 *
 * @example
 * ```typescript
 * export const sequenceContract = defineContract('sequences', {
 *     getSequences: route({ method: 'GET', path: '/', ... }),
 * });
 *
 * // Root contract — routes at service root, accessible as activitiesApi.activities
 * export const activityContract = defineContract('activities', { ... }, { root: true });
 * ```
 */
export function defineContract<N extends string, T extends ServiceContract>(
	name: N,
	contract: T,
	options?: DefineContractOptions
): T & { readonly [CONTRACT_META]: ContractMeta<N> };
export function defineContract<T extends ServiceContract>(contract: T): T;
export function defineContract(...args: any[]): any {
	if (typeof args[0] === 'string') {
		const [name, contract, options] = args;
		const result = { ...contract };
		Object.defineProperty(result, CONTRACT_META, {
			value: { name, ...(options?.root && { root: true }) },
			enumerable: false,
		});
		return result;
	}
	return args[0];
}

/**
 * Derive the URL prefix from a contract's metadata.
 * Returns `/${name}` for named contracts, or '' for root/unnamed contracts.
 */
export function getContractPrefix(contract: ServiceContract): string {
	if (CONTRACT_META in contract) {
		const meta = (contract as any)[CONTRACT_META] as ContractMeta;
		if (meta.root) return '';
		return meta.name ? `/${meta.name}` : '';
	}
	return '';
}
